/**
 * FR-018.7 — the group editor for a shared name: one source label (a `field_span` mapping) that
 * covers two or more fields (TENANT_CONFIGURATION_UI.md §5.1, §7).
 *
 * From a harvest year X the farmer either keeps the name shared with changed members (untick a
 * field, add one) or stops sharing — the name means one field from X. History before X stays
 * shared. Confirm writes ONE batch (see `groupItems.ts`): each member's span row restated to end
 * at X-1, plus the new span rows from X or one `field` row from X.
 *
 * The batch is built from the row's `span_rows` (EVERY live span row of the label — F-16
 * supersedes by label, not by target), and a client guard blocks Confirm when the preview would
 * supersede any row the batch does not account for (`supersedeGuard.ts`). Without `span_rows` (an
 * older backend) Confirm stays off and the panel asks for a reload.
 *
 * Preview → confirm → job is the mapping editor's lifecycle (`usePreviewConfirm.ts`).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContextPanel } from '@hofsync/design-system';
import type { LenseConfig } from '../../../../services/twinService';
import type { SourceRowDto, SourceSearchHit } from '../../../../services/sourcesService';
import { useEntitySources, useSourceSearch } from '../../../../hooks/useSources';
import { useSourceSystems } from '../../../../hooks/useSourceSystems';
import { sourcesConfigFor } from './sourcesConfig';
import { activeTargets, buildGroupItems, groupYearBounds, namedTargets, type GroupMode } from './groupItems';
import { previewSentences, type Translate } from './previewSentences';
import {
    confirmFailureMessage,
    errorKindSentence,
    hasUnrestatedSupersedes,
    useConfirmJob,
    useDebouncedPreview,
    type SubmitConfirm,
} from './usePreviewConfirm';
import { MappingJobView } from './MappingJobView';
import { UnrestatedNotice } from './UnrestatedNotice';
import './MappingEditorPanel.css';
import './GroupEditorPanel.css';

export interface GroupEditorPanelProps {
    open: boolean;
    /** The `shared` row the group editor was opened on. */
    row: SourceRowDto | null;
    entityType: string;
    entityId: string;
    lens?: LenseConfig;
    onClose(): void;
    onNavigate?(entityType: string, entityId: string): void;
}

const SEARCH_DEBOUNCE_MS = 250;
const FIELD = 'field';

function useT(): Translate {
    const { t } = useTranslation();
    return t as unknown as Translate;
}

export const GroupEditorPanel: React.FC<GroupEditorPanelProps> = ({ open, row, entityType, entityId, lens, onClose, onNavigate }) => {
    const t = useT();
    // A new row starts a fresh editor (and forgets the last job); closing (row → null) keeps it.
    const [lastRow, setLastRow] = React.useState<SourceRowDto | null>(row);
    const [session, setSession] = React.useState(0);
    // Kept in this always-mounted component: a confirm resolving after Esc still records its job.
    const { job, setJob, jobQuery, submitConfirm, submitting } = useConfirmJob();
    if (row !== lastRow) {
        setLastRow(row);
        if (row) {
            setSession((s) => s + 1);
            setJob(null);
        }
    }
    const { data } = useEntitySources(entityType, entityId, open && !!row);
    const noun = data?.config?.noun || sourcesConfigFor(lens).noun;

    if (!open || !row) return null;
    const title = t('farm.sources.group.title', 'Gemeinsame Zuordnung');
    const subtitle = data?.object?.name ?? undefined;

    if (job) {
        return (
            <MappingJobView
                title={title}
                subtitle={subtitle}
                job={job}
                jobQuery={jobQuery}
                noun={noun}
                onClose={onClose}
                onNavigate={onNavigate}
            />
        );
    }
    return (
        <GroupForm
            key={session}
            row={row}
            entityId={entityId}
            noun={noun}
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            submitConfirm={submitConfirm}
            submitting={submitting}
            onNavigate={onNavigate}
        />
    );
};

interface GroupFormProps {
    row: SourceRowDto;
    entityId: string;
    noun: string;
    title: string;
    subtitle?: string;
    onClose(): void;
    submitConfirm: SubmitConfirm;
    submitting: boolean;
    /** Opens the object an unrestated row belongs to (R2). */
    onNavigate?(entityType: string, entityId: string): void;
}

const GroupForm: React.FC<GroupFormProps> = ({ row, entityId, noun, title, subtitle, onClose, submitConfirm, submitting, onNavigate }) => {
    const t = useT();
    const uid = React.useId();
    const systems = useSourceSystems();
    const currentYear = new Date().getFullYear();

    const spanRows = row.span_rows;
    const bounds = groupYearBounds(spanRows, currentYear);

    const [mode, setMode] = React.useState<GroupMode>('keep');
    const [from, setFrom] = React.useState(() => Math.min(Math.max(currentYear, bounds.min), bounds.max));
    const [unticked, setUnticked] = React.useState<Set<string>>(() => new Set());
    const [added, setAdded] = React.useState<SourceSearchHit[]>([]);
    const [fieldId, setFieldId] = React.useState<string | null>(null);
    const [q, setQ] = React.useState('');
    const [debouncedQ, setDebouncedQ] = React.useState('');
    const [message, setMessage] = React.useState<string | null>(null);
    // Part of a batch landed (partial failure): the preview it was confirmed from is spent, so Confirm
    // stays off until a FRESH preview (a new key: the auto re-preview, or an edit) has resolved.
    const [spentPreviewKey, setSpentPreviewKey] = React.useState<string | null>(null);

    React.useEffect(() => {
        const h = window.setTimeout(() => setDebouncedQ(q.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(h);
    }, [q]);
    const searchQ = mode === 'keep' && debouncedQ.length >= 2 ? debouncedQ : '';
    const search = useSourceSearch(FIELD, searchQ);
    const active = activeTargets(spanRows, from);
    const addedTargets = added
        .filter((a) => !active.some((t) => t.entityId === a.entity_id))
        .map((a) => ({ entityId: a.entity_id, name: a.name }));
    const listed = [...active, ...addedTargets];
    const choices = namedTargets(spanRows);
    // Deleted (tombstoned) fields of the group: shown by a placeholder, never by id.
    const deletedCount = new Set((spanRows ?? []).filter((r) => r.name === null).map((r) => r.target_ref)).size;
    const known = new Set(listed.map((m) => m.entityId));
    const hits = (search.data ?? []).filter((h) => !known.has(h.entity_id));

    const result = buildGroupItems({
        sourceSystem: row.source_system,
        sourceLabel: row.source_label,
        spanRows,
        added: addedTargets,
        from,
        mode,
        unticked,
        fieldId,
    });
    const items = result.status === 'ready' ? result.items : null;
    const itemsJson = items ? JSON.stringify(items) : null;
    const { current, currentKey, error: previewError, refresh: refreshPreview } = useDebouncedPreview(itemsJson);
    const landed = spentPreviewKey !== null && spentPreviewKey === currentKey;

    // A new batch clears the last confirm's sentence; a re-preview of the same batch keeps it.
    React.useEffect(() => {
        setMessage(null);
    }, [itemsJson]);

    const nameOf = (id: string | null) => (id && [...choices, ...listed].find((m) => m.entityId === id)?.name) || '';
    const sharingNames = listed.filter((m) => !unticked.has(m.entityId)).map((m) => m.name);
    const resultName = mode === 'stop' ? nameOf(fieldId) : t('farm.sources.group.resultShared', 'geteilt von {{names}}', { names: sharingNames.join(', ') });

    const sentences = current ? previewSentences(current, t, { noun }) : null;
    // Every row the preview supersedes must be one this batch restates or replaces (F-16 is by label).
    const accounted = result.status === 'ready' ? result.accountedIds : [];
    const surprise = !!current && hasUnrestatedSupersedes(current, accounted);
    const canConfirm =
        !landed &&
        !!items &&
        !!current &&
        !!sentences &&
        !sentences.noop &&
        sentences.blocking.length === 0 &&
        !surprise &&
        !submitting;

    const handleConfirm = () => {
        if (!items || !current || landed) return;
        setMessage(null);
        const targetId = mode === 'stop' && fieldId ? fieldId : entityId;
        submitConfirm({ items, preview: current, accounted }, { targetType: FIELD, targetId, targetKind: 'entity' }, (failure) => {
            if (failure.landed) setSpentPreviewKey(currentKey);
            setMessage(confirmFailureMessage(failure, t));
            if (failure.repreview) refreshPreview();
        });
    };

    const toggle = (id: string) =>
        setUnticked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const systemName = (id: string | null) => (id && systems.find((s) => s.id === id)?.display_name) || id || '';

    // --- (1) The span label ---
    const referenceSection = (
        <section className="mapping-editor__section">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.reference', 'Referenz in der Quelle')}</h3>
            <div className="mapping-editor__reference">
                {row.source_system && <span className="mapping-editor__pill">{systemName(row.source_system)}</span>}
                <span className="mapping-editor__label">{row.source_label}</span>
            </div>
            <p className="mapping-editor__meta">
                {t('farm.sources.group.sharedBy', 'Diesen Namen teilen sich {{n}} Felder.', { n: active.length })}
            </p>
        </section>
    );

    // --- (2) From harvest year + (3) keep / stop ---
    const yearOptions: number[] = [];
    for (let y = Math.min(bounds.min, from); y <= Math.max(bounds.max, from); y++) yearOptions.push(y);
    const modeSection = (
        <section className="mapping-editor__section">
            <div className="mapping-editor__field">
                <label htmlFor={`${uid}-from`}>{t('farm.sources.group.fromYear', 'Ab Erntejahr')}</label>
                <select id={`${uid}-from`} value={String(from)} onChange={(e) => setFrom(Number(e.target.value))}>
                    {yearOptions.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>
            </div>
            <div className="mapping-editor__fields" role="radiogroup">
                <label className="mapping-editor__radio">
                    <input type="radio" name={`${uid}-mode`} checked={mode === 'keep'} onChange={() => setMode('keep')} />
                    {t('farm.sources.group.keepSharing', 'Weiter gemeinsam (die Felder ändern sich ab diesem Jahr)')}
                </label>
                <label className="mapping-editor__radio">
                    <input type="radio" name={`${uid}-mode`} checked={mode === 'stop'} onChange={() => setMode('stop')} />
                    {t('farm.sources.group.stopSharing', 'Nicht mehr gemeinsam: der Name meint ein Feld')}
                </label>
            </div>
        </section>
    );

    // --- (4) Members (keep) or the one field (stop) ---
    const memberSection =
        mode === 'keep' ? (
            <section className="mapping-editor__section">
                <h3 className="mapping-editor__heading">{t('farm.sources.group.members', 'Felder')}</h3>
                <ul className="mapping-editor__members">
                    {listed.map((m) => (
                        <li key={m.entityId}>
                            <label className="mapping-editor__radio">
                                <input type="checkbox" checked={!unticked.has(m.entityId)} onChange={() => toggle(m.entityId)} />
                                {m.name}
                            </label>
                        </li>
                    ))}
                    {Array.from({ length: deletedCount }, (_, i) => (
                        <li key={`deleted-${i}`} className="mapping-editor__meta">
                            {t('farm.sources.group.deletedField', 'Gelöschtes Feld')}
                        </li>
                    ))}
                </ul>
                <div className="mapping-editor__search">
                    <input
                        type="search"
                        aria-label={t('farm.sources.group.addField', 'Feld hinzufügen')}
                        placeholder={t('farm.sources.group.addField', 'Feld hinzufügen')}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    {searchQ && search.error ? (
                        <p className="mapping-editor__meta">
                            {t('farm.sources.errors.sources_search_unavailable', 'Die Suche ist gerade nicht verfügbar.')}
                        </p>
                    ) : searchQ && search.data && hits.length === 0 ? (
                        <p className="mapping-editor__meta">{t('farm.sources.editor.noHits', 'Keine Treffer.')}</p>
                    ) : null}
                    {searchQ && hits.length > 0 && (
                        <ul className="mapping-editor__hits">
                            {hits.map((hit) => (
                                <li key={hit.entity_id}>
                                    <button
                                        type="button"
                                        className="mapping-editor__hit"
                                        onClick={() => {
                                            setAdded((prev) => [...prev, hit]);
                                            setUnticked((prev) => {
                                                const next = new Set(prev);
                                                next.delete(hit.entity_id);
                                                return next;
                                            });
                                            setQ('');
                                            setDebouncedQ('');
                                        }}
                                    >
                                        <span className="mapping-editor__hit-name">{hit.name}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        ) : (
            <section className="mapping-editor__section">
                <div className="mapping-editor__field">
                    <label htmlFor={`${uid}-field`}>{t('farm.sources.group.whichField', 'Welches Feld?')}</label>
                    <select id={`${uid}-field`} value={fieldId ?? ''} onChange={(e) => setFieldId(e.target.value || null)}>
                        <option value="">{t('farm.sources.group.fieldPlaceholder', 'Feld wählen')}</option>
                        {choices.map((m) => (
                            <option key={m.entityId} value={m.entityId}>
                                {m.name}
                            </option>
                        ))}
                    </select>
                </div>
            </section>
        );

    // --- (5) The year strip: shared before X, the result from X ---
    const earliest = bounds.min;
    const stripStart = Math.max(Math.min(earliest, from), from - 4);
    const strip: number[] = [];
    for (let y = stripStart; y <= from + 2; y++) strip.push(y);
    const legend = [
        from > earliest ? t('farm.sources.group.legendShared', 'bis {{year}} geteilt', { year: from - 1 }) : null,
        resultName ? t('farm.sources.group.legendFrom', 'ab {{year}} → {{result}}', { year: from, result: resultName }) : null,
    ]
        .filter(Boolean)
        .join(' · ');
    const stripSection = (
        <section className="mapping-editor__section">
            <div className="mapping-editor__strip" aria-hidden="true">
                {strip.map((y) => (
                    <span
                        key={y}
                        className={`mapping-editor__year${y >= from ? ' mapping-editor__year--move' : y >= earliest ? ' mapping-editor__year--stay' : ''}`}
                    >
                        {y}
                    </span>
                ))}
            </div>
            {legend && <p className="mapping-editor__meta">{legend}</p>}
        </section>
    );

    // --- (6) What will happen ---
    let body: React.ReactNode;
    if (result.status === 'reload') {
        body = (
            <p className="mapping-editor__reason">
                {t('farm.sources.group.reload', 'Diese Gruppe lässt sich mit dem geladenen Stand nicht sicher ändern. Bitte Seite neu laden.')}
            </p>
        );
    } else if (result.status === 'blocked' && result.reason === 'deleted') {
        body = (
            <p className="mapping-editor__reason">
                {t(
                    'farm.sources.group.deletedBlocks',
                    'Ein Feld dieser Gruppe wurde gelöscht. Die Gruppe kann erst geändert werden, wenn es wiederhergestellt oder die Zuordnung vom Support entfernt wurde.',
                )}
            </p>
        );
    } else if (result.status === 'blocked') {
        body = (
            <p className="mapping-editor__reason">
                {t(
                    'farm.sources.group.splitBefore',
                    'Ab {{split}} ist dieser Name bereits neu aufgeteilt. Wählen Sie ein Ab-Erntejahr ab {{year}}.',
                    { split: result.split, year: result.year },
                )}
            </p>
        );
    } else if (result.status === 'unsupported') {
        body = (
            <p className="mapping-editor__reason">
                {t('farm.sources.editor.noSystem', 'Diese Schreibweise stammt aus Belegen und hat keine Zuordnung, die sich ändern ließe.')}
            </p>
        );
    } else if (result.status === 'incomplete') {
        const copy =
            result.reason === 'field'
                ? t('farm.sources.group.chooseField', 'Wählen Sie das Feld, das der Name ab diesem Jahr meint.')
                : result.reason === 'members'
                  ? t('farm.sources.group.needTwo', 'Ein gemeinsamer Name braucht mindestens zwei Felder – sonst „Nicht mehr gemeinsam“ wählen.')
                  : t('farm.sources.group.unchanged', 'Haken Sie ein Feld ab oder fügen Sie eines hinzu, um die Auswirkung zu sehen.');
        body = <p className="mapping-editor__meta">{copy}</p>;
    } else if (previewError) {
        body = (
            <p className="mapping-editor__reason">
                {errorKindSentence(t, previewError.kind, 'farm.sources.errors.sources_preview_unavailable', 'Die Vorschau ist gerade nicht verfügbar.')}
            </p>
        );
    } else if (!sentences) {
        body = <p className="mapping-editor__meta">{t('farm.sources.editor.previewLoading', 'Die Auswirkung wird berechnet …')}</p>;
    } else if (sentences.noop) {
        body = <p className="mapping-editor__reason">{t('farm.sources.editor.noop', 'Keine Änderung – diese Zuordnung besteht bereits.')}</p>;
    } else {
        body = (
            <>
                {sentences.blocking.map((s) => (
                    <p key={s} className="mapping-editor__reason" role="alert">
                        {s}
                    </p>
                ))}
                {surprise && current && (
                    <UnrestatedNotice
                        preview={current}
                        accounted={accounted}
                        entityType={FIELD}
                        entityId={entityId}
                        t={t}
                        onNavigate={onNavigate}
                    />
                )}
                <ul className="mapping-editor__sentences">
                    {sentences.sentences.map((s) => (
                        <li key={s}>{s}</li>
                    ))}
                </ul>
            </>
        );
    }
    const previewSection = (
        <section className="mapping-editor__section" aria-live="polite">
            <h3 className="mapping-editor__heading">{t('farm.sources.editor.whatHappens', 'Was passiert')}</h3>
            {body}
            {message && (
                <p className="mapping-editor__reason" role="alert">
                    {message}
                </p>
            )}
        </section>
    );

    const footer = (
        <div className="mapping-editor__footer">
            <button type="button" className="mapping-editor__btn mapping-editor__btn--outline" onClick={onClose}>
                {t('farm.sources.actions.cancel', 'Abbrechen')}
            </button>
            <button type="button" className="mapping-editor__btn mapping-editor__btn--primary" disabled={!canConfirm} onClick={handleConfirm}>
                {t('farm.sources.actions.confirm', 'Zuordnung bestätigen')}
            </button>
        </div>
    );

    return (
        <ContextPanel isOpen onClose={onClose} title={title} subtitle={subtitle} width="medium" footerSlot={footer}>
            <div className="mapping-editor">
                {referenceSection}
                {modeSection}
                {memberSection}
                {stripSection}
                {previewSection}
            </div>
        </ContextPanel>
    );
};

export default GroupEditorPanel;
